// Static editorial content for the You Are Not Alone site.
// Plain-English explanations based on UK child protection law (England & Wales).
// This is general information, not legal advice.

export const understandingTopics = [
  {
    id: "social-services-involvement",
    title: "When social services first become involved",
    summary:
      "What a referral is, who can make one, and what to expect in the first days and weeks.",
    body: [
      "A referral to children's services can come from many places — a school, a GP, a health visitor, the police, a neighbour, or even an anonymous caller. Once a referral is made, the local authority has a duty under section 47 of the Children Act 1989 to decide whether further enquiries are needed.",
      "In the first 24 to 72 hours, a duty social worker will usually review what has been reported and decide one of three things: take no further action, offer support on a voluntary basis (often called a 'Child in Need' plan under section 17), or open a formal investigation if there is reasonable cause to suspect a child is suffering, or is likely to suffer, significant harm.",
      "It is normal to feel watched, judged, or panicked at this stage. You are entitled to know — in writing — what the concerns are, what is being considered, and who you can speak to. Ask. Write things down. Keep a folder.",
    ],
  },
  {
    id: "child-protection-conference",
    title: "Child protection conferences explained",
    summary:
      "What an Initial CP Conference is, who attends, and how decisions are made.",
    body: [
      "An Initial Child Protection Conference is a meeting called by the local authority when professionals believe a child may be at continuing risk of significant harm. It is usually held within 15 working days of a strategy discussion.",
      "Attendees normally include: an independent chair, the social worker and their manager, the school or nursery, the GP or health visitor, the police where relevant, and you — the parents or carers. You are entitled to attend, to bring a supporter or advocate, and in most cases to read the report being presented before the meeting.",
      "The conference decides whether the child should be made the subject of a Child Protection Plan, under one of four categories: neglect, physical abuse, emotional abuse, or sexual abuse. A plan does not mean your child will be removed — it means a multi-agency plan will be put in place to reduce risk.",
      "You can disagree with what is recorded. Ask for your disagreement to be noted formally in the minutes. You can also request a complaint or escalation through the Local Authority Designated Officer (LADO) or the independent chair.",
    ],
  },
  {
    id: "court-orders",
    title: "Court orders — what each one actually means",
    summary:
      "Section 20, EPO, Interim Care Orders, Care Orders, Placement Orders and Adoption Orders.",
    body: [
      "Section 20 (voluntary accommodation): Your child is looked after by the local authority with your agreement. You retain parental responsibility. You can, in principle, withdraw consent — though it is wise to take legal advice before doing so.",
      "Emergency Protection Order (EPO): A short order, usually up to 8 days, granted by a court when there is reasonable cause to believe a child is likely to suffer significant harm if not removed.",
      "Interim Care Order (ICO): Granted during court proceedings while assessments take place. The local authority shares parental responsibility with you. The child may be placed with you, a relative, foster carers, or in residential care.",
      "Care Order: A final order made under section 31 of the Children Act 1989. The local authority shares parental responsibility and can decide where the child lives and many aspects of their day-to-day life. A Care Order lasts until the child is 18 unless discharged.",
      "Placement Order: Authorises the local authority to place a child for adoption without parental consent. This is one of the most serious orders the state can make.",
      "Adoption Order: Permanently severs the legal relationship between a child and their birth family. It is the most final order in family law.",
    ],
  },
  {
    id: "your-rights",
    title: "Your rights as a parent or carer",
    summary:
      "What you are legally entitled to ask for, see, and challenge.",
    body: [
      "You have the right to legal advice. In care proceedings, legal aid is non-means and non-merits tested for parents and anyone with parental responsibility — that is, free, regardless of income.",
      "You have the right to see the documents that will be used in any conference or hearing about your child, in good time, so that you can respond. If something is being withheld, you can ask why.",
      "You have the right to bring a McKenzie Friend or advocate to most meetings. You can ask to record meetings — the local authority may refuse, but you can keep your own written notes and ask for a copy of theirs.",
      "You have the right to make a formal complaint about the conduct of a social worker or a service. There is a three-stage statutory complaints process for children's services in England.",
      "You have the right to be treated with dignity. The Public Sector Equality Duty (Equality Act 2010) applies to local authorities. So does Article 8 of the European Convention on Human Rights — the right to respect for private and family life.",
    ],
  },
  {
    id: "court-hearings",
    title: "What to expect at a family court hearing",
    summary:
      "From the first directions hearing to the final hearing, in plain English.",
    body: [
      "Family court proceedings under the Public Law Outline are designed to be completed within 26 weeks, though many take longer. There are usually several hearings: a Case Management Hearing, an Issues Resolution Hearing, and a Final Hearing.",
      "Hearings are normally held in private. The judge, the local authority's solicitor and barrister, your solicitor and barrister, the children's guardian and their solicitor, and you will be present. Witnesses give evidence one at a time.",
      "You will likely be cross-examined. This is the part many parents find hardest. A good barrister will prepare you carefully. Speak slowly. It is fine to say 'I don't remember' or 'I don't understand the question'.",
      "At the final hearing, the judge weighs the evidence and decides what order (if any) to make. They must give reasons. You are entitled to a copy of the written judgment. Increasingly, judges are publishing anonymised judgments — you can ask whether yours will be.",
    ],
  },
  {
    id: "assessments",
    title: "Assessments — parenting, psychological, viability",
    summary:
      "What different assessments are, what to expect, and how to prepare.",
    body: [
      "A parenting assessment looks at your capacity to meet your child's needs, usually over several sessions. Be honest. Ask what model is being used and what its limitations are. Ask to see and comment on the draft.",
      "A psychological assessment is carried out by a clinical psychologist, usually as a court-directed expert. You are entitled to know the questions they have been asked. You can comment on the report before it is finalised.",
      "A viability assessment is a short assessment of a family member or friend as a potential carer. A positive viability is usually followed by a full Special Guardianship or Connected Persons assessment.",
      "If you disagree with an assessment, you do not have to accept it. You can ask your solicitor about an Independent Social Work assessment or a second expert opinion, though the court must grant permission.",
    ],
  },
];

export const glossary = [
  { term: "Section 17", definition: "Children Act 1989 power for local authorities to provide services to children 'in need'. Voluntary, supportive, no court involvement." },
  { term: "Section 20", definition: "Voluntary accommodation by the local authority. You keep parental responsibility and can in principle withdraw consent." },
  { term: "Section 47", definition: "The legal threshold and duty to investigate where there is reasonable cause to suspect a child is suffering, or likely to suffer, significant harm." },
  { term: "Strategy discussion", definition: "A multi-agency meeting (often a phone call) to decide whether section 47 enquiries should start." },
  { term: "Initial CP Conference (ICPC)", definition: "Multi-agency meeting deciding whether a child needs a Child Protection Plan." },
  { term: "Child Protection Plan", definition: "A written multi-agency plan to reduce risk to a child. Categorised as Neglect, Physical, Emotional or Sexual." },
  { term: "PLO (Public Law Outline)", definition: "The pre-proceedings letter and meeting stage, before the local authority issues care proceedings." },
  { term: "EPO", definition: "Emergency Protection Order — short, urgent removal order, normally up to 8 days." },
  { term: "ICO", definition: "Interim Care Order — gives the local authority shared parental responsibility during proceedings." },
  { term: "Care Order", definition: "Final order under s.31 of the Children Act 1989. Lasts until age 18 unless discharged." },
  { term: "Placement Order", definition: "Authorises the local authority to place a child for adoption without parental consent." },
  { term: "Special Guardianship Order (SGO)", definition: "An order giving a relative or carer enhanced parental responsibility, often used for kinship care." },
  { term: "Children's Guardian", definition: "A Cafcass officer appointed to represent the child's interests in court." },
  { term: "McKenzie Friend", definition: "A lay supporter who can sit with you in court, take notes, and quietly offer advice — they do not normally have the right to speak." },
  { term: "LADO", definition: "Local Authority Designated Officer — handles allegations against people who work with children." },
  { term: "Cafcass", definition: "Children and Family Court Advisory and Support Service. Represents children in family court proceedings in England." },
  { term: "Pre-proceedings letter", definition: "A formal letter from the local authority warning that court proceedings are being considered, inviting you to a meeting with a solicitor." },
  { term: "Threshold criteria", definition: "The legal test the local authority must meet to obtain a care or supervision order — significant harm attributable to parental care." },
];

export const supportDirectory = [
  {
    name: "Family Rights Group",
    description: "Free confidential advice line for parents and family members involved with children's services. The most established specialist service in England and Wales.",
    url: "https://frg.org.uk",
    type: "Advice & advocacy",
    region: "England & Wales",
  },
  {
    name: "Coram Children's Legal Centre",
    description: "Free legal advice on children's law, including the Child Law Advice line.",
    url: "https://childlawadvice.org.uk",
    type: "Legal advice",
    region: "England",
  },
  {
    name: "Rights of Women",
    description: "Free, confidential legal advice for women, including family law specialists.",
    url: "https://rightsofwomen.org.uk",
    type: "Legal advice",
    region: "England & Wales",
  },
  {
    name: "Grandparents Plus / Kinship",
    description: "Support, advice and community for kinship carers — grandparents, aunts, uncles and family friends raising children.",
    url: "https://kinship.org.uk",
    type: "Kinship support",
    region: "England & Wales",
  },
  {
    name: "Mind",
    description: "Mental health support and information. Often the first call families make when overwhelmed.",
    url: "https://mind.org.uk",
    type: "Emotional support",
    region: "UK",
  },
  {
    name: "Samaritans",
    description: "24/7 emotional support line. Free to call from any phone — 116 123.",
    url: "https://samaritans.org",
    type: "Crisis support",
    region: "UK",
  },
  {
    name: "Resolution",
    description: "Find a family law solicitor committed to a constructive, non-adversarial approach.",
    url: "https://resolution.org.uk",
    type: "Find a solicitor",
    region: "England & Wales",
  },
  {
    name: "Law Society — Find a Solicitor",
    description: "Official directory of regulated solicitors. Filter by 'Children law' to find specialists.",
    url: "https://solicitors.lawsociety.org.uk",
    type: "Find a solicitor",
    region: "England & Wales",
  },
  {
    name: "Citizens Advice",
    description: "Free, independent advice on rights, benefits, housing, and legal options.",
    url: "https://citizensadvice.org.uk",
    type: "General advice",
    region: "UK",
  },
  {
    name: "Pause",
    description: "Supports women who have had — or are at risk of having — more than one child removed from their care.",
    url: "https://pause.org.uk",
    type: "Specialist support",
    region: "England",
  },
];

export const stories = [
  {
    id: "s1",
    pen_name: "Sarah, Yorkshire",
    role: "Mother",
    title: "I didn't understand what 'section 47' meant until it was over",
    excerpt:
      "When the letter came through the door, I read it three times. I still didn't know what it meant. It took a friend, and eventually a solicitor, to translate it into English.",
  },
  {
    id: "s2",
    pen_name: "Mark, the Midlands",
    role: "Father",
    title: "Being a father in this system",
    excerpt:
      "I was an afterthought in my own children's case for months. I had to fight to be included in meetings I should have been at by right.",
  },
  {
    id: "s3",
    pen_name: "Joan, South Wales",
    role: "Grandmother and kinship carer",
    title: "We took our grandchildren in overnight. The support never came.",
    excerpt:
      "We said yes immediately. We would say yes again. But nobody told us how lonely it would be, or how little help we would get.",
  },
];

export const blogPosts = [
  {
    id: "b1",
    title: "What the £15.5 billion really tells us",
    date: "2026-01-22",
    excerpt:
      "Children's social care in England spent £15.5 billion in 2024-25. That number deserves to be looked at clearly — not to attack the people doing impossible work, but to ask whether the system around them is doing what we hope it is.",
    tag: "Campaign",
  },
  {
    id: "b2",
    title: "Why we publish in plain English",
    date: "2026-01-08",
    excerpt:
      "Most parents we hear from say the same thing. Nobody explained what was happening to them. Plain English is not a nice-to-have. It is the difference between feeling like a participant and feeling like a number.",
    tag: "Our approach",
  },
  {
    id: "b3",
    title: "Transparency and trust — the case for more open courts",
    date: "2025-12-14",
    excerpt:
      "The Transparency Pilot has opened a window into the family courts. We look at what is changing, what isn't, and what families tell us they still need.",
    tag: "Transparency",
  },
];
